// vue-i18n 9 (Vue 3) exposes `t()` on i18n.global, not on the i18n
// instance directly. Call `i18n.global.t(...)` from non-component
// code — this avoids `i18n.t is not a function` when the service
// worker's updated() callback fires after a deploy.
import { i18n } from "../src/boot/i18n";
import { Dark, Notify } from "quasar";
import { register } from "register-service-worker";

let serviceWorkerRegistration = null;
const INSTALL_WAIT_TIMEOUT_MS = 30000;

const waitForInstall = (worker) =>
  new Promise((resolve) => {
    if (!worker || worker.state !== "installing") {
      resolve();
      return;
    }

    const cleanup = () => {
      worker.removeEventListener("statechange", onStateChange);
      clearTimeout(timeout);
    };

    const onStateChange = () => {
      if (worker.state !== "installing") {
        cleanup();
        resolve();
      }
    };

    const timeout = setTimeout(() => {
      cleanup();
      resolve();
    }, INSTALL_WAIT_TIMEOUT_MS);

    worker.addEventListener("statechange", onStateChange);
  });

const checkForPwaUpdates = async () => {
  const registration = serviceWorkerRegistration;
  if (!registration) {
    return { status: "unavailable" };
  }

  let updateFound = false;
  const onUpdateFound = () => {
    updateFound = true;
  };
  registration.addEventListener("updatefound", onUpdateFound);

  try {
    await registration.update();
  } catch (error) {
    registration.removeEventListener("updatefound", onUpdateFound);
    return { status: "error", error };
  }

  // Give any late-dispatched updatefound a tick to register before we decide.
  await Promise.resolve();

  const installing = registration.installing;
  if (installing) {
    await waitForInstall(installing);
  }

  registration.removeEventListener("updatefound", onUpdateFound);

  if (updateFound || registration.waiting || registration.installing) {
    return { status: "updated" };
  }

  return { status: "checked" };
};

if (typeof window !== "undefined") {
  window.checkForPwaUpdates = checkForPwaUpdates;
}

// The ready(), registered(), cached(), updatefound() and updated()
// events passes a ServiceWorkerRegistration instance in their arguments.
// ServiceWorkerRegistration: https://developer.mozilla.org/en-US/docs/Web/API/ServiceWorkerRegistration

register(process.env.SERVICE_WORKER_FILE, {
  // The registrationOptions object will be passed as the second argument
  // to ServiceWorkerContainer.register()
  // https://developer.mozilla.org/en-US/docs/Web/API/ServiceWorkerContainer/register#Parameter

  // registrationOptions: { scope: './' },

  ready() {
    if (process.env.DEV) {
      console.log("App is being served from cache by a service worker.");
    }
  },

  registered(registration) {
    serviceWorkerRegistration = registration;
    if (process.env.DEV) {
      console.log("Service worker has been registered.");
    }
  },

  cached(/* registration */) {
    if (process.env.DEV) {
      console.log("Content has been cached for offline use.");
    }
  },

  updatefound(/* registration */) {
    if (process.env.DEV) {
      console.log("New content is downloading.");
    }
  },

  updated(/* registration */) {
    if (!process.env.DEV) {
      const bg = Dark.isActive ? "grey-1" : "grey-10";
      const fg = Dark.isActive ? "grey-10" : "grey-1";
      Notify.create({
        message: i18n.global.t("success.updateAvailable"),
        icon: "update",
        color: bg,
        textColor: fg,
        position: "top",
        timeout: 0,
        actions: [
          {
            label: i18n.global.t("Update"),
            color: "primary",
            handler: () => {
              window.location.reload();
            },
          },
          {
            label: i18n.global.t("Dismiss"),
            color: "primary",
            handler: () => {},
          },
        ],
      });
    }
  },

  offline() {
    if (process.env.DEV) {
      console.log(
        "No internet connection found. App is running in offline mode."
      );
    }
  },

  error(err) {
    console.error("Error during service worker registration:", err);
  },
});
